import { useCallback } from 'react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import type { ExtendedSeriesType } from '@/components/Chart/types/highcharts';
import { exportChartImage } from '@/components/Chart/utils/exportChartImage';
import getUnitSuffix from '@/components/Chart/utils/getUnitSuffix';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { buildExportFileDateSuffix } from '@/utils/exportFileDateSuffix';

import type { ExportingMimeTypeValue } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

const CAIRO_FONT_URL = '/fonts/Cairo.ttf';

const getSeriesNamesWithUnits = (series: ExtendedSeriesType[]) =>
  series.map(s => ({ name: `${s.name}${getUnitSuffix(s.userOptions.custom)}` }));

const useChartImageExport = () => {
  const { formatDateString } = useDateText();
  const { chartRef } = useChartContext();

  const getDateSuffix = useCallback(() => {
    const nowIso = new Date().toJSON();
    const date = formatDateString(nowIso, DateTextVariants.FULL);
    return buildExportFileDateSuffix(date, nowIso);
  }, [formatDateString]);

  const onExport = useCallback(
    (type: ExportingMimeTypeValue): Promise<void> => {
      const chart = chartRef.current?.chart;
      if (!chart) return Promise.resolve();

      const baseOptions = chart.options.exporting?.chartOptions ?? chart.options;

      const exportingOptions =
        type === 'application/pdf'
          ? {
              local: true,
              sourceWidth: 1200,
              pdfFont: {
                normal: CAIRO_FONT_URL,
                bold: CAIRO_FONT_URL,
                italic: CAIRO_FONT_URL,
                bolditalic: CAIRO_FONT_URL
              }
            }
          : { sourceWidth: 1200 };

      return exportChartImage({
        chart,
        type,
        filename: `GX-Chart-${getDateSuffix()}`,
        options: {
          ...baseOptions,
          legend: {
            ...(baseOptions as Highcharts.Options).legend,
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            padding: 20,
            itemWidth: undefined,
            itemStyle: {
              ...(baseOptions as Highcharts.Options).legend?.itemStyle,
              fontSize: '10px'
            }
          },
          series: getSeriesNamesWithUnits(chart.series as ExtendedSeriesType[]),
          // Pass export type so exportEvents.load() can branch on it
          exporting: { type }
        },
        exportingOptions
      });
    },
    [chartRef, getDateSuffix]
  );

  return { onExport };
};

export default useChartImageExport;
